import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { billingService } from '../../services/billing';
import styles from './ProfilePage.module.css';

const PRODUCT_LABELS = { analytics: 'Analytics', erp: 'ERP', benchmark: 'Benchmark IQ' };

const TIER_LABELS = { B: 'Beta', A: 'Alpha', 'A+': 'Alpha+' };

const STATUS_COPY = {
  active: { label: 'Premium · Active', className: styles.billingStatusActive },
  expired: { label: 'Expired', className: styles.billingStatusExpired },
  free: { label: 'Free', className: styles.billingStatusFree },
};

const ORDER_STATUS_COPY = {
  paid: { label: 'Paid', className: styles.billingStatusActive },
  failed: { label: 'Failed', className: styles.billingStatusExpired },
  created: { label: 'Pending', className: styles.billingStatusFree },
};

const formatAmount = (paise) =>
  `₹${(Number(paise || 0) / 100).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

const formatDate = (value) =>
  value
    ? new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';

const BillingSection = () => {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setBilling(await billingService.getMyBilling());
    } catch (err) {
      setError(err.message || 'Failed to load billing');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return <p className={styles.billingMuted}>Loading billing…</p>;
  }

  if (error) {
    return (
      <div className={styles.infoItem}>
        <span className={styles.value}>{error}</span>
        <button type="button" className={styles.passwordButton} onClick={load} style={{ marginTop: 12 }}>
          Retry
        </button>
      </div>
    );
  }

  const plan = billing?.plan || { status: 'free', products: [] };
  const orders = billing?.orders || [];
  const status = STATUS_COPY[plan.status] || STATUS_COPY.free;
  const products = (plan.products || []).map((p) => PRODUCT_LABELS[p] || p).join(', ');
  const tierLabel = plan.analyticsTierLabel || TIER_LABELS[plan.productTier] || 'Beta';

  return (
    <>
      <div className={styles.infoGrid}>
        <div className={styles.infoItem}>
          <label className={styles.label}>Plan status</label>
          <span className={`${styles.billingStatus} ${status.className}`}>{status.label}</span>
        </div>
        <div className={styles.infoItem}>
          <label className={styles.label}>Analytics tier</label>
          <span className={styles.value}>{tierLabel}</span>
        </div>
        <div className={styles.infoItem}>
          <label className={styles.label}>Products</label>
          <span className={styles.value}>{products || 'None'}</span>
        </div>
        <div className={styles.infoItem}>
          <label className={styles.label}>
            {plan.status === 'expired' ? 'Expired on' : 'Paid through'}
          </label>
          <span className={styles.value}>
            {plan.status === 'free' ? 'No paid plan yet' : formatDate(plan.paidThrough || plan.currentPeriodEnd)}
          </span>
        </div>
        <div className={styles.infoItem}>
          <label className={styles.label}>Actions</label>
          <button type="button" className={styles.passwordButton} onClick={() => navigate('/pricing')}>
            {plan.status === 'active' ? 'Renew or change plan' : plan.status === 'expired' ? 'Renew plan' : 'Upgrade'}
          </button>
        </div>
      </div>

      <h3 className={styles.billingSubtitle}>Payment history</h3>
      {orders.length === 0 ? (
        <p className={styles.billingMuted}>No payments yet.</p>
      ) : (
        <div className={styles.billingTableWrap}>
          <table className={styles.billingTable}>
            <thead>
              <tr>
                <th>Date</th>
                <th>Plan</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Period</th>
                <th>Payment ID</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((order) => {
                const orderStatus = ORDER_STATUS_COPY[order.status] || ORDER_STATUS_COPY.created;
                return (
                  <tr key={order.id}>
                    <td>{formatDate(order.paidAt || order.createdAt)}</td>
                    <td>
                      {(order.products || [])
                        .map((product) =>
                          product === 'analytics' && order.analyticsTierLabel
                            ? `Analytics (${order.analyticsTierLabel})`
                            : PRODUCT_LABELS[product] || product
                        )
                        .join(' + ') || '—'}
                    </td>
                    <td>{formatAmount(order.amountPaise)}</td>
                    <td>
                      <span
                        className={`${styles.billingStatus} ${orderStatus.className}`}
                        title={order.failureReason || undefined}
                      >
                        {orderStatus.label}
                      </span>
                    </td>
                    <td>
                      {order.periodStart
                        ? `${formatDate(order.periodStart)} – ${formatDate(order.periodEnd)}`
                        : '—'}
                    </td>
                    <td className={styles.billingMono}>{order.razorpayPaymentId || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
};

export default BillingSection;
